import { TableCell } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getRankHeat, getRankHeatStops, getSponsoredRankHeatStops } from "./heatScale";
import { RankVariantBadge } from "./RankVariantBadge";
import { SiblingRankBadge } from "@/components/shared/SiblingRankBadge";
import { formatDayLabel, NO_READING_LABEL } from "./constants";

export type RankHeatCellVariant = "rollup" | "keyword";

/** Which heat family the painted block samples. `organic` is the default — byte-for-byte today. */
export type RankHeatScale = "organic" | "sponsored";

/**
 * The sponsored reading to stack under a keyword cell's organic block in Both view (FR-7). `null`
 * `position` takes the same gray no-reading treatment as any other missing reading; the marker is
 * shown only on a day whose `fromVariant` is `true` and that has a painted sponsored reading.
 */
export interface SponsoredStrip {
  position: number | null;
  fromVariant: boolean | null;
  variantAsin: string | null;
}

export interface RankHeatCellProps {
  /**
   * The wire's `positions[i]` for this row on this day -- an integer >= 1, or `null` for "no
   * reading". `getRankHeat` throws `RangeError` for anything else (a `0`/`-1` sentinel that has not
   * yet been mapped to `null` upstream -- FEATURE-003 maps it server-side and is shipping in
   * parallel with this task). This cell never hands such a value to `getRankHeat`: anything that is
   * not `null` and not a finite number >= 1 renders exactly as "no reading" would, so one bad value
   * paints one cell grey instead of taking the whole grid down.
   */
  position: number | null;
  /** `days[i]` for this column, ISO `YYYY-MM-DD`. Used only to phrase the no-reading/tooltip text --
   * never parsed into a `Date`. */
  day: string;
  variant: RankHeatCellVariant;
  /** Required for `variant="rollup"`: how many keywords sit beneath this row. Unused for `"keyword"`. */
  keywordCount?: number;
  /** Required for `variant="rollup"`: `counts[i]` -- how many of those keywords had a reading this
   * day. This is the only render surface for `counts[i]`
   * (`decisions/a-roll-up-position-is-the-mean-of-what-was-read-that-day.md`). Unused for
   * `"keyword"` -- a keyword row carries no `counts` on the wire; its own `positions` is the coverage. */
  count?: number;
  /**
   * WEB-004. The heat family the primary block paints with. `"organic"` (the default) reuses
   * `getRankHeatStops()` and is identical to the cell today; `"sponsored"` paints the indigo ramp
   * from `getSponsoredRankHeatStops()` for a Sponsored-view keyword cell (FR-8). Only an organic
   * default is ever passed on product/theme rows.
   */
  scale?: RankHeatScale;
  /**
   * WEB-004 / FR-10. The per-day variant marker on the **primary** block — rendered only on a painted
   * sponsored keyword cell whose `fromVariant` is `true`. Organic cells never pass these, so the
   * marker never appears on an organic reading (the FR-10 carve-out).
   */
  fromVariant?: boolean | null;
  variantAsin?: string | null;
  /**
   * WEB-004 / FR-7. Both view only: the thin sponsored strip stacked under this keyword cell's
   * organic block. Absent on every other cell, which keeps the organic/rollup render path unchanged.
   */
  strip?: SponsoredStrip;
  /**
   * WEB-004 / FR-7. Sponsored view only: product/theme rows keep their organic reading but dim to
   * 35% opacity. Absent/false everywhere else, so the base cell is unchanged.
   */
  dim?: boolean;
  /** Keyword variant only: the sibling ASIN that holds this day's rank, when it is not the hero's own. */
  siblingAsin?: string | null;
}

/** `true` only for a value `getRankHeat` accepts -- a finite number >= 1. `null`, `0`, a negative
 * number and anything non-finite all take the no-reading treatment instead of reaching `getRankHeat`,
 * which throws for everything below 1. */
function isPaintableRank(position: number | null): position is number {
  return position !== null && Number.isFinite(position) && position >= 1;
}

/**
 * The painted block inside a heat cell -- a fixed-height chip for the primary reading, or the thin
 * `h-4` strip for a Both-view sponsored reading. Factored so the strip and the primary block share
 * one painting path; with `strip={false}` and no marker, the output is identical to the chip this
 * cell has always rendered (the Organic-view byte-for-byte guarantee).
 */
function HeatBlock({
  position,
  scale,
  isRollup,
  strip,
  fromVariant,
  variantAsin,
  siblingAsin,
}: {
  position: number | null;
  scale: RankHeatScale;
  isRollup: boolean;
  strip: boolean;
  fromVariant?: boolean | null;
  variantAsin?: string | null;
  siblingAsin?: string | null;
}) {
  const painted = isPaintableRank(position);
  // The stops are read only for a painted cell -- a no-reading cell never touches `tokens.css`, the
  // same laziness the cell has always had (a null cell renders with no token fixture in a test).
  const style = painted
    ? (() => {
        const stops = scale === "sponsored" ? getSponsoredRankHeatStops() : getRankHeatStops();
        const heat = getRankHeat(position, stops);
        return { backgroundColor: heat.background, color: heat.ink };
      })()
    : undefined;

  const marker =
    painted && fromVariant && variantAsin ? <RankVariantBadge variantAsin={variantAsin} /> : null;
  // AUT-443: the organic sibling-ASIN marker. The caller passes siblingAsin only for an organic
  // primary block (organic and Both views), never a sponsored cell, so it never collides with the
  // sponsored variant marker above.
  const siblingMarker =
    painted && !isRollup && siblingAsin ? <SiblingRankBadge asin={siblingAsin} className="-mr-1" /> : null;

  return (
    <div
      className={cn(
        // Item 11 of the design-conformance pass: the colour lives on a fixed-height chip inside the
        // cell, never on the `<td>` itself. The chip's height (32px, `h-8`) is constant regardless of
        // row level. The strip is the prototype's `h-4 leading-4` sponsored block (signal-v3
        // `RankHeatmapTable.tsx` `HeatBlock`): 16px, stacked under the organic chip in Both view.
        strip
          ? "mx-auto flex h-4 w-[50px] items-center justify-center rounded-[2px] text-center text-xs leading-4 tabular-nums"
          : "mx-auto flex h-8 w-[50px] items-center justify-center rounded-[2px] text-center tabular-nums",
        (marker || siblingMarker) && "gap-0.5",
        !painted && "bg-muted text-muted-foreground",
        // AUT-523: product/theme (roll-up) cells paint the SAME solid heat colour as keyword cells.
        // The only brightness reduction is the Sponsored-view 35% dim (the `dim` prop below), never an
        // always-on roll-up fade.
      )}
      style={style}
    >
      {painted ? position : NO_READING_LABEL}
      {marker}
      {siblingMarker}
    </div>
  );
}

export function RankHeatCell({
  position,
  day,
  variant,
  keywordCount,
  count,
  scale = "organic",
  fromVariant,
  variantAsin,
  strip,
  dim,
  siblingAsin,
}: RankHeatCellProps) {
  const painted = isPaintableRank(position);
  const isRollup = variant === "rollup";
  const dayLabel = formatDayLabel(day);

  const rollupTooltip = isRollup
    ? count !== undefined && count > 0
      ? `Average of ${count} of ${keywordCount} keywords read on ${dayLabel}`
      : `None of this row's ${keywordCount} keywords had a reading on ${dayLabel}`
    : null;

  const noReadingLabel = `No reading on ${dayLabel}`;
  // A roll-up cell's accessible name is always its tooltip text; a keyword cell only ever names
  // itself when it has no reading -- a painted keyword cell's integer content speaks for itself.
  const accessibleLabel = isRollup ? rollupTooltip : !painted ? noReadingLabel : undefined;

  const primary = (
    <HeatBlock
      position={position}
      scale={scale}
      isRollup={isRollup}
      strip={false}
      fromVariant={fromVariant}
      variantAsin={variantAsin}
      siblingAsin={siblingAsin}
    />
  );

  // The organic/rollup path (no strip, no dim) wraps nothing around the chip -- it is exactly the
  // cell today. Both view adds the stacked sponsored strip; Sponsored view adds the 35% dim wrapper
  // on product/theme rows. The two never coexist on one cell (keyword rows strip, roll-up rows dim).
  const body = strip ? (
    <div className="space-y-0.5">
      {primary}
      <HeatBlock
        position={strip.position}
        scale="sponsored"
        isRollup={false}
        strip
        fromVariant={strip.fromVariant}
        variantAsin={strip.variantAsin}
      />
    </div>
  ) : dim ? (
    <div className="opacity-[0.35]">{primary}</div>
  ) : (
    primary
  );

  const cell = (
    <TableCell
      className="w-[54px] px-0.5 py-1 align-middle"
      title={!isRollup ? accessibleLabel : undefined}
      aria-label={accessibleLabel}
    >
      {body}
    </TableCell>
  );

  if (!isRollup) {
    return cell;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{cell}</TooltipTrigger>
      <TooltipContent side="top">{rollupTooltip}</TooltipContent>
    </Tooltip>
  );
}
